import { type StrictlyFrozenMap, mapFreeze } from "./maps";
import { type StrictlyFrozenSet, setFreeze } from "./sets";
import { useState } from "react";

type FrozenDisp<TCold, TWarm> = React.Dispatch<TCold | TWarm | ((prevState: TCold) => TCold | TWarm)>;
type Frozen<TCold extends object, TWarm> = [TCold, FrozenDisp<TCold, TWarm>];

/**
 * Returns a frozen (read-only) stateful value, and a setter function to update the value.
 * As a side effect, the initial state, and any values passed to the setter function, will be frozen using Object.freeze.
 * All states (initial or otherwise) must be objects (not null or undefined).
 *
 * @see https://reactjs.org/docs/hooks-reference.html#usestate
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
 */
export function useFrozenState<TSetEntry>(init: Set<TSetEntry>): Frozen<StrictlyFrozenSet<TSetEntry>, typeof init>;
export function useFrozenState<X, Y>(init: Map<X, Y>): Frozen<StrictlyFrozenMap<X, Y>, typeof init>;
export function useFrozenState<TOther extends object>(init: TOther): Frozen<Readonly<TOther>, typeof init>;
export function useFrozenState<TOther extends object, TSetEntry, X, Y>(
    initialState: TOther | Set<TSetEntry> | Map<X, Y>,
) {
    const [state, setter] = useState(initialState);
    return [freeze(state), setter] as const;
}

type Warm<TOther, TSetEntry, X, Y> = TOther | Set<TSetEntry> | Map<X, Y>;
type Cold<TOther, TSetEntry, X, Y> = Readonly<TOther> | StrictlyFrozenSet<TSetEntry> | StrictlyFrozenMap<X, Y>;

function freeze<TSetEntry>(value: Set<TSetEntry>): StrictlyFrozenSet<TSetEntry>;
function freeze<X, Y>(value: Map<X, Y>): StrictlyFrozenMap<X, Y>;
function freeze<TOther>(value: TOther): Readonly<TOther>;
function freeze<TOther, TSetEntry, X, Y>(value: Warm<TOther, TSetEntry, X, Y>): Cold<TOther, TSetEntry, X, Y> {
    if (value instanceof Set) {
        // special case for sets
        return setFreeze(value);
    } else if (value instanceof Map) {
        // special case for maps
        return mapFreeze(value);
    } else {
        // regular freeze
        assertObject(value);
        return Object.freeze(value);
    }
}

function assertObject(value: unknown): asserts value is object {
    if (typeof value !== "object" || value === null) {
        throw new Error("frozen state values must be non-null objects");
    }
}

export type FrozenSetDispatch<T> = Frozen<StrictlyFrozenSet<T>, Set<T>>[1];

export type FrozenMapDispatch<X, Y> = Frozen<StrictlyFrozenMap<X, Y>, Map<X, Y>>[1];
